import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { CountBadge } from '../../../../components/CountBadge';
import { FolderTabs } from '../../../../components/FolderTabs';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import type {
  PersonnelDetail,
  PersonnelSaln,
  SalnBusinessInterest,
  SalnLiability,
  SalnPersonalProperty,
  SalnRealProperty,
} from '../personnelTypes';

/**
 * Which schedule the preview is showing.
 *
 * The same four keys in the same order as `<EditSalnModal>`'s own `ScheduleKey`, because the
 * two surfaces draw one strip of folders. They are deliberately NOT shared from a common
 * module: the editor keys draft state off its copy and this keys a read, so a future fifth
 * schedule has to be added to both — which is a compile error in each, rather than one file
 * silently teaching the other about a folder it cannot render.
 */
type SalnScheduleKey =
  | 'realProperties'
  | 'personalProperties'
  | 'liabilities'
  | 'businessInterests';

/**
 * SALN — the Statement of Assets, Liabilities and Net Worth filed annually under RA 6713.
 *
 * **Captured from the hospital system on 17 Sep 2026**, and filed at the END of the 201 file
 * group rather than inside the form's own run: a SALN is not a section of CS Form 212, it is a
 * separate statutory filing that a 201 file holds — so it appends after the last form section,
 * exactly as the appending rule requires.
 *
 * **One row per YEAR declared, most recent first.** The panel is a history: the question asked
 * of it is "what did they declare, and when", and the newest filing is the one a reader is
 * almost always after.
 *
 * **The three money columns are the SERVER's arithmetic, never this panel's.** They arrive on
 * the record as derived values over the schedules, and recomputing them here would be a second
 * sum that disagrees with the one in the modal the first time a line is corrected. Nothing in
 * this file adds anything up.
 *
 * **Searched, sorted and paged in memory**, which is correct here: the rule against client-side
 * filtering is about a PAGED list whose filter applies only to the rows on screen. These arrive
 * whole with the record, so the filter sees everything there is.
 */
export function SalnPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (row: PersonnelSaln) => void;
  onDelete: (row: PersonnelSaln) => void;
  onBulkDelete: (rows: PersonnelSaln[]) => void;
  onRestore: (row: PersonnelSaln) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const rows = useMemo(
    () => (showDeleted ? record.salns : record.salns.filter((x) => x.isActive)),
    [record.salns, showDeleted],
  );

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      /*
        The search reads the YEAR and the REMARKS, and it also reaches into the schedules — the
        question a reader brings to this panel is usually "which year did they declare the
        condo in", and a filter that could not see a line's description would answer it with an
        empty table while the line sat one click away.

        This is the same call the L&D panel takes on its joined competency names: a value that
        is not a column can still be the thing somebody searches for.
      */
      const matched = term
        ? rows.filter((x) =>
            [
              String(x.asOfYear),
              x.remarks ?? '',
              ...x.realProperties.map((r) => `${r.description} ${r.exactLocation ?? ''}`),
              ...x.personalProperties.map((r) => r.description),
              ...x.liabilities.map((r) => `${r.nature} ${r.creditorName ?? ''}`),
              ...x.businessInterests.map((r) => `${r.entityName} ${r.natureOfInterest ?? ''}`),
            ].some((value) => value.toLowerCase().includes(term)),
          )
        : rows;

      const field = query.sort?.replace(/^-/, '') ?? 'asOfYear';
      const descending = query.sort?.startsWith('-') ?? false;
      const direction = descending ? -1 : 1;

      const sorted = [...matched].sort((a, b) => {
        /*
          The money columns sort as NUMBERS, or 9,000 lands above 85,000. Ties break on the year
          and then on the creation stamp, so an amended filing for a year already declared keeps
          a stable place instead of swapping with the original between two reads.
        */
        const numeric =
          field === 'asOfYear'
            ? a.asOfYear - b.asOfYear
            : field === 'totalAssets'
              ? a.totalAssets - b.totalAssets
              : field === 'totalLiabilities'
                ? a.totalLiabilities - b.totalLiabilities
                : field === 'netWorth'
                  ? a.netWorth - b.netWorth
                  : null;

        if (numeric !== null) {
          const gap = numeric * direction;
          return gap !== 0 ? gap : a.createdAt.localeCompare(b.createdAt);
        }

        return a.asOfYear - b.asOfYear;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  const state = useDataTable<PersonnelSaln>({
    fetchPage,
    pageSize: 10,

    // Most recent year first, matching the order the server sends them in — or the profile and
    // its own table would disagree about which row is first.
    defaultSort: '-asOfYear',
  });

  const columns: DataColumn<PersonnelSaln>[] = [
    {
      /*
        The ANCHOR is the YEAR, which is what identifies a filing: a SALN is referred to as "the
        2025 SALN" and by nothing else. It renders mono because a year is a machine value — and
        NOT `numeric`, which dims a zero and is for quantities.
      */
      key: 'asOfYear',
      header: 'As of year',
      width: 120,
      sortable: true,
      render: (x) => <TextCell value={String(x.asOfYear)} mono />,
    },
    {
      key: 'totalAssets',
      header: 'Total assets',
      width: 160,
      sortable: true,
      summary: true,
      align: 'right',
      /*
        `formatMoney`, `mono numeric`, right-aligned — the money rule, and `numeric` is what
        mutes a zero. A filing that declared nothing really did total nothing, which is a
        different fact from a field nobody filled in, so this is never an em dash.
      */
      render: (x) => <TextCell value={formatMoney(x.totalAssets)} mono numeric />,
    },
    {
      key: 'totalLiabilities',
      header: 'Total liabilities',
      width: 160,
      sortable: true,
      summary: true,
      align: 'right',
      render: (x) => (
        <TextCell value={formatMoney(x.totalLiabilities)} mono numeric />
      ),
    },
    {
      key: 'netWorth',
      header: 'Net worth',
      width: 160,
      sortable: true,
      summary: true,
      align: 'right',
      render: (x) => <TextCell value={formatMoney(x.netWorth)} mono numeric />,
    },
    {
      /*
        A COUNT, because the Connections schedule counts toward no total — a business interest is
        a connection to be disclosed rather than an amount. `numeric` still applies: a filing
        that declared none reads 0 and dims, which is exactly right.
      */
      key: 'connections',
      header: 'Connections',
      width: 130,
      align: 'right',
      render: (x) => (
        <TextCell value={String(x.businessInterests.length)} mono numeric />
      ),
    },
    {
      key: 'isJointFiling',
      header: 'Filing',
      width: 120,
      render: (x) => <TextCell value={x.isJointFiling ? 'Joint' : 'Individual'} />,
    },
    {
      key: 'remarks',
      header: 'Remarks',
      width: 260,
      render: (x) => <TextCell value={x.remarks ?? null} />,
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row carries
        no marker while Show deleted is on, which is the one state a row must never be silent
        about. `<DataTablePanel>` moves it to the end of the data columns itself.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelSaln>
        id="personnel-saln"
        state={state}
        /*
          The first column opens the row as a key/value preview, built from these same `columns`
          so it cannot drift from the table or miss one somebody adds.
        */
        rowPreview={{
          title: 'SALN filing',
          /*
            THE FOUR SCHEDULES, under the key/value rows the columns produce. A column can
            only ever say how LONG a schedule is — this table's `Connections` column is
            literally that — so before this the preview of a filing showed three totals and
            not one of the lines they were totalled from, which is the whole content of a
            SALN. The totals stay where they are; this is what they are the sum of.

            NO PER-SCHEDULE TOTAL IS DRAWN HERE, and that is deliberate rather than an
            omission. The server sends the three filing-level figures and no per-schedule
            ones, so a total under each table would be this panel doing arithmetic over a
            SAVED filing — the second sum that disagrees with the stored one the first time
            a line is corrected. The modal totals a DRAFT because a draft has no server
            figure to read; a saved filing has one, and it is already in the rows above.
          */
          details: (x) => <SalnSchedules filing={x} />,

          /*
            The EDITOR'S OWN SIZE. `<EditSalnModal>` is `3xl` and this was `lg`, so opening a
            filing and then correcting it changed the dialog out from under the reader — and at
            `lg` the seven-column real-property schedule would have been a horizontal scroll in
            a third of the room the same table has in the form.
          */
          size: '3xl' as const,
        }}
        columns={columns}
        rowKey={(x) => x.id}
        searchPlaceholder="Search year, remarks or a declared line"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        rowActions={(x) => [
          {
            key: `edit-${x.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: x.isActive
              ? null
              : 'This filing is deleted. Restore it before editing.',
            onClick: () => onEdit(x),
          },
          x.isActive
            ? {
                key: `delete-${x.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(x),
              }
            : {
                key: `restore-${x.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(x),
              },
        ]}
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive);

          return [
            {
              key: 'delete-salns',
              label: `Delete ${deletable.length} filing${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected filing is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No SALN filings recorded yet."
        emptySearchMessage="No filing matches that search."
      />
    </ProfileSection>
  );
}
/**
 * The four schedules of one filing, read-only, under its preview.
 *
 * **It is the EDIT MODAL'S OWN LAYOUT, read-only** — the same four folders, in the same order,
 * with the same words and the same counts in the tabs, standing on the same slate-100 container
 * over the same white panel. A reader who opens a filing and then corrects it must not watch
 * the thing reorganise itself: the schedules they were looking at are where they left them.
 * Four stacked tables would have shown the same data and made the reader find it twice.
 *
 * It maps onto the editor exactly, because the preview is built the same way round: the
 * filing's OWN facts — year, joint filing, remarks, the totals — sit above the strip there as a
 * form and here as the column-derived key/value table, and the folders hold the schedules in
 * both. What changes inside a folder is only that `<InlineAddTable>` becomes `<PreviewGrid>`.
 *
 * **Every column the editor collects is shown**, with the editor's own headings. The panel's
 * columns can say a filing totals a number; they cannot say what any of it is.
 *
 * **An empty schedule still has a folder, and the folder says it is empty.** A nil declaration
 * is a fact about a statutory filing and usually the one somebody is checking for; a folder
 * that vanished would leave a reader unable to tell it from a section this preview omits.
 *
 * **NO PER-SCHEDULE TOTAL, which is where the layout deliberately stops matching.** The editor
 * draws one under each table because a DRAFT has no server figure to read. A saved filing does
 * — three of them, in the rows above — so totalling the lines here would be the second sum that
 * disagrees with the stored one the first time a line is corrected. `Connections` never had one
 * at all: an interest is a connection to be disclosed, not an amount.
 */
function SalnSchedules({ filing }: { filing: PersonnelSaln }) {
  const [folder, setFolder] = useState<SalnScheduleKey>('realProperties');

  return (
    /*
      The editor's own container, class for class: one padded slate-100 box holding the strip
      AND the panel, because the two must share a left edge or the active tab's merge lands over
      the panel's curve instead of its straight top line.
    */
    <div className="rounded-panel bg-slate-100 px-3 pb-3 pt-2">
      <FolderTabs<SalnScheduleKey>
        id="saln-preview-folders"
        ariaLabel="SALN schedules"
        paddingX="px-3"
        activeKey={folder}
        onChange={setFolder}
        /*
          The count rides INSIDE the label through CountBadge, as the editor's does, and a zero
          renders — that is what lets a reader see which schedules hold anything before clicking.
        */
        tabs={[
          {
            key: 'realProperties',
            label: (
              <>
                Real properties <CountBadge count={filing.realProperties.length} />
              </>
            ),
          },
          {
            key: 'personalProperties',
            label: (
              <>
                Personal properties <CountBadge count={filing.personalProperties.length} />
              </>
            ),
          },
          {
            key: 'liabilities',
            label: (
              <>
                Liabilities <CountBadge count={filing.liabilities.length} />
              </>
            ),
          },
          {
            key: 'businessInterests',
            label: (
              <>
                Connections <CountBadge count={filing.businessInterests.length} />
              </>
            ),
          },
        ]}
      />

      <div className="rounded-b-panel border border-t-0 border-slate-300 bg-white p-3">
        {/*
          NO TITLE on the grid: the tab above it already names the collection and carries its
          count, so a heading here would be the same value in two slots one line apart.
        */}
        {folder === 'realProperties' ? (
          <PreviewGrid<SalnRealProperty>
            rows={filing.realProperties}
            rowKey={(r) => r.id}
            empty="No real property declared."
            columns={[
              {
                header: 'Description',
                width: 220,
                render: (r) => <TextCell value={r.description} />,
              },
              {
                header: 'Location',
                width: 200,
                render: (r) => <TextCell value={r.exactLocation ?? null} />,
              },
              {
                // A YEAR is an identifier, so mono and never numeric — numeric mutes a zero,
                // which is a treatment for quantities.
                header: 'Acq. year',
                width: 90,
                align: 'right',
                render: (r) => <TextCell value={year(r.acquisitionYear)} mono />,
              },
              {
                header: 'Mode',
                width: 130,
                render: (r) => <TextCell value={r.acquisitionMode ?? null} />,
              },
              {
                header: 'Assessed',
                width: 130,
                align: 'right',
                render: (r) => <TextCell value={money(r.assessedValue)} mono numeric />,
              },
              {
                header: 'Market',
                width: 130,
                align: 'right',
                render: (r) => <TextCell value={money(r.fairMarketValue)} mono numeric />,
              },
              {
                header: 'Acq. cost',
                width: 140,
                align: 'right',
                render: (r) => <TextCell value={money(r.acquisitionCost)} mono numeric />,
              },
            ]}
          />
        ) : null}

        {folder === 'personalProperties' ? (
          <PreviewGrid<SalnPersonalProperty>
            rows={filing.personalProperties}
            rowKey={(r) => r.id}
            empty="No personal property declared."
            columns={[
              {
                header: 'Description',
                width: 360,
                render: (r) => <TextCell value={r.description} />,
              },
              {
                header: 'Year acquired',
                width: 140,
                align: 'right',
                render: (r) => <TextCell value={year(r.yearAcquired)} mono />,
              },
              {
                header: 'Acquisition cost',
                width: 180,
                align: 'right',
                render: (r) => <TextCell value={money(r.acquisitionCost)} mono numeric />,
              },
            ]}
          />
        ) : null}

        {folder === 'liabilities' ? (
          <PreviewGrid<SalnLiability>
            rows={filing.liabilities}
            rowKey={(r) => r.id}
            empty="No liability declared."
            columns={[
              { header: 'Nature', width: 300, render: (r) => <TextCell value={r.nature} /> },
              {
                header: 'Creditor',
                width: 240,
                render: (r) => <TextCell value={r.creditorName ?? null} />,
              },
              {
                header: 'Outstanding balance',
                width: 200,
                align: 'right',
                render: (r) => <TextCell value={money(r.outstandingBalance)} mono numeric />,
              },
            ]}
          />
        ) : null}

        {folder === 'businessInterests' ? (
          <PreviewGrid<SalnBusinessInterest>
            rows={filing.businessInterests}
            rowKey={(r) => r.id}
            empty="No business interest or financial connection declared."
            columns={[
              { header: 'Entity', width: 240, render: (r) => <TextCell value={r.entityName} /> },
              {
                header: 'Address',
                width: 240,
                render: (r) => <TextCell value={r.businessAddress ?? null} />,
              },
              {
                header: 'Nature of interest',
                width: 200,
                render: (r) => <TextCell value={r.natureOfInterest ?? null} />,
              },
              {
                // A CALENDAR date, so the conversion-free formatter: the instant path would
                // shift an acquisition a day back for a reader west of UTC.
                header: 'Acquired',
                width: 140,
                render: (r) => (
                  <TextCell
                    value={r.acquisitionDate ? formatDateOnly(r.acquisitionDate) : null}
                    mono
                  />
                ),
              },
            ]}
          />
        ) : null}
      </div>
    </div>
  );
}

/** A quantity the filer left blank renders as the grid's em dash, never as a zero they declared. */
function money(value: number | null | undefined): string | null {
  return value === null || value === undefined ? null : formatMoney(value);
}

/** Likewise a year: absent is absent, and 0 would be a year nobody wrote. */
function year(value: number | null | undefined): string | null {
  return value === null || value === undefined ? null : String(value);
}
